import "./dashboard.scss";

import { ReportRankModel } from "@core/model/report-rank-model.interface";
import SimpleDropdown from "@core/ui/simple-dropdown/simple-dropdown";
import { GetResponseReportRank } from "@factory/model/query-model-report";
import { useQyGetBranch } from "@factory/query/branch.query";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useQyGetLevel } from "@factory/query/level.query";
import {
  useQyGetCampaignReport,
  useQyGetCardStatsReport,
  useQyGetEarnPointsReport,
  useQyGetEarnStampReport,
  useQyGetRankReport,
  useQyGetRedeemPointsReport,
  useQyGetRedeemStampReport,
} from "@factory/query/report.query";
import { DashboardModel } from "@shared/models/dashboard-model";
import { LabelValue } from "@shared/models/label-value.interface";
import DashboardCard from "@shared/ui/dashboard-card/dashboard-card";
import TextBlock from "@shared/ui/text-block/text-block";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { getRandomInt } from "@shared/utility/number-manipulate";
import classNames from "classnames";
import { DataView } from "primereact/dataview";
import { Dropdown } from "primereact/dropdown";
import { ListBox } from "primereact/listbox";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { useCallback, useState } from "react";
import { FaChartPie } from "react-icons/fa6";

import AgeDashboard from "./age-dashboard/age-dashboard";
import { colorArray } from "./color-option";

export interface DashboardProps { }

export function Dashboard() {
  const [rowLimit, setRowLimit] = useState(20);
  const [, setPageNumber] = useState(0);
  const [first, setFirst] = useState(0);
  const [rank, setRank] = useState<ReportRankModel[]>([]);
  const [rankFilter, setRankFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("points");
  const [sortDir, setSortDir] = useState<string>("asc");
  const [branch, setBranch] = useState<string>("");

  const getColorBg = useCallback(() => {
    const index = getRandomInt(0, 8);
    const color = colorArray[index];
    return color;
  }, []);

  const { data: cardStatusResponse } = useQyGetCardStatsReport();
  const cardStatus = cardStatusResponse?.data?.map(
    (item) =>
      new DashboardModel(
        item.label || "-",
        item.value,
        <FaChartPie />,
        undefined,
        undefined,
        getColorBg(),
      ),
  );

  const handleRankSuccess = async (response: GetResponseReportRank) => {
    if (!response.data?.length) return;
    const rows = response.data as unknown as ReportRankModel[];
    setRank(rows);
    const imageAdded = await Promise.all(
      rows.map(async (element) => {
        if (!element.level_avatar) return element;
        const blob = await viewAsset(element.level_avatar);
        return { ...element, image: URL.createObjectURL(blob) };
      }),
    );
    setRank(imageAdded);
  };
  const { data: rankResponse } = useQyGetRankReport(
    20,
    0,
    rankFilter,
    sortBy,
    sortDir,
    undefined,
    undefined,
    undefined,
    true,
    handleRankSuccess,
  );

  const { data: campaignResponse } = useQyGetCampaignReport(branch);
  const campaignData = campaignResponse as LabelValue[];

  const { data: earnResponse } = useQyGetEarnPointsReport(branch);
  const earnData = earnResponse as LabelValue[];

  const { data: stampResponse } = useQyGetEarnStampReport(branch);
  const stampData = stampResponse as LabelValue[];

  const { data: redeemPointsResponse } = useQyGetRedeemPointsReport(branch);
  const redeemPointData = redeemPointsResponse as LabelValue[];

  const { data: redeemStampResponse } = useQyGetRedeemStampReport(branch);
  const redeemStampData = redeemStampResponse as LabelValue[];

  const { data: utilLevelResponse } = useQyGetLevel("");
  const utilLevelOptions = LabelValue.MapArray(
    utilLevelResponse?.data || [],
    (item) => `${item.name} (${item.points} Points | ${item.stamp} Stamps)`,
    (item) => item.code,
  );

  const { data: branchResponse } = useQyGetBranch("");
  const branchOptions = LabelValue.MapArray(
    branchResponse?.data || [],
    (item) => item.name,
    (item) => item.code,
  );

  // Local Functions
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  // Local Renders
  const optionTemplate = (option: LabelValue) => {
    return (
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            "rounded-full p-2 w-11 h-11 flex items-center justify-center text-sm font-bold",
            getColorBg(),
          )}
        >
          {option.value}
        </div>
        <div>{option.label}</div>
      </div>
    );
  };
  const gridItem = (item: ReportRankModel) => {
    return (
      <div className="flex items-center shadow-md rounded-md overflow-hidden border p-4">
        {item?.image ? (
          <img src={item?.image} className="h-20 w-20 object-cover" />
        ) : (
          <Skeleton size="4rem" />
        )}

        <div className="flex flex-col w-full">
          <section className="flex">
            <div className="pl-4 py-1 flex-grow flex justify-start flex-col items-start">
              <h4>
                {item.first_name} {item.last_name}
              </h4>
              <TextBlock
                label="Total Amount"
                value={currencyFormat(item.total_amount, "PHP")}
              />
              <TextBlock label="Level Name" value={item.level_name} />
            </div>
          </section>
        </div>
      </div>
    );
  };
  const listTemplate = (items: ReportRankModel[]): any => {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mt-4`}>
        {items.map((item, id) => (
          <div key={id}>{gridItem(item)}</div>
        ))}
      </div>
    );
  };

  return (
    <div id="Dashboard" className="dashboard p-7">
      <section className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h5 className="text-left">Card Usage Stats</h5>
          <SimpleDropdown
            value={branch}
            name="branch"
            options={branchOptions}
            onChange={(e) => {
              setBranch(e.value);
            }}
            label="Filter by Branch"
            className="w-1/3 md:w-1/5"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cardStatus?.map((item, id) => (
            <DashboardCard key={id} model={item} />
          ))}
        </div>
      </section>

      <section className="flex flex-col mt-6">
        <div className="flex justify-between gap-4 items-center">
          <h5 className="text-left">Rank</h5>
          <div className="w-1/3 md:w-1/4">
            <SimpleDropdown
              value={rankFilter}
              name="rankFilter"
              options={utilLevelOptions}
              onChange={(e) => {
                setRankFilter(e.value);
              }}
              label="Filter Rank"
            />
          </div>
        </div>
        <DataView
          value={rank}
          listTemplate={listTemplate}
          paginator
          rows={rowLimit}
          first={first}
          totalRecords={rankResponse?.count}
          rowsPerPageOptions={[10, 20, 30]}
          onPage={onPageChange}
        />
      </section>

      <section className="flex flex-col gap-4 mt-6">
        <h5 className="text-left">Top 10 Favorites</h5>
        <section className="grid grid-cols-3 gap-4">
          <div className="flex flex-col border border-gray-300 bg-white p-4 rounded-md">
            <h4 className="text-center">Point Earners</h4>

            <ListBox
              options={earnData}
              itemTemplate={optionTemplate}
              className="border-0"
            />
          </div>
          <div className="flex flex-col border border-gray-300 bg-white p-4 rounded-md">
            <h4 className="text-center">Stamp Earners</h4>

            <ListBox
              options={stampData}
              itemTemplate={optionTemplate}
              className="border-0"
            />
          </div>
          <div className="flex flex-col border border-gray-300 bg-white p-4 rounded-md">
            <h4 className="text-center">Point Spender</h4>

            <ListBox
              options={redeemPointData}
              itemTemplate={optionTemplate}
              className="border-0"
            />
          </div>
          <div className="flex flex-col border border-gray-300 bg-white p-4 rounded-md">
            <h4 className="text-center">Stamp Spender</h4>

            <ListBox
              options={redeemStampData}
              itemTemplate={optionTemplate}
              className="border-0"
            />
          </div>
          <div className="flex flex-col border border-gray-300 bg-white p-4 rounded-md">
            <h4 className="text-center">Loved Campaigns</h4>

            <ListBox
              options={campaignData}
              itemTemplate={optionTemplate}
              className="border-0"
            />
          </div>
        </section>
      </section>

      <section className="flex flex-col gap-4 mt-6">
        <h5 className="text-left">Age Range</h5>
        <AgeDashboard />
      </section>
    </div>
  );
}

export default Dashboard;
